import { store } from './store.js';
/**
 * SESSION & NAVIGATION MANAGER - Detectimáticas de 1º
 * Gestión de pantallas, estados de sesión y flujo del juego.
 */

const SessionManager = {
    // Session state variable
    state: {
        problems: [],           // 5 problemas generados
        currentProblem: 0,      // índice 0-4
        answers: {},            // respuestas del usuario {0: {userAnswer, isCorrect, wizardData}, ...}
        score: 0,               // puntuación (0-5)
        solvedProblemIds: [],    // IDs de problemas ya resueltos en esta sesión
        startTime: null,        // timestamp inicio sesión
        endTime: null           // timestamp fin sesión
    },

    init() {
        console.log('🚀 Inicializando window.SessionManager...');
        store.state = this.state; // Mantener compatibilidad global
    },

    hideAllScreens() {
        // Detener cualquier audio al cambiar de pantalla
        if (window.stopAudio) window.stopAudio();
        else if (window.speechSynthesis) window.speechSynthesis.cancel();

        // Cancelar el temporizador de auto-reproducción de la narrativa si está pendiente
        if (window.NarrativeManager && window.NarrativeManager.autoPlayTimer) {
            clearTimeout(window.NarrativeManager.autoPlayTimer);
            window.NarrativeManager.autoPlayTimer = null;
        }

        const screens = ['splashScreen', 'loginScreen', 'welcomeScreen', 'configScreen', 'narrativeScreen', 'loadingScreen', 'problemScreen', 'summaryScreen', 'mapScreen', 'guideScreen', 'storeScreen'];
        screens.forEach(screenId => {
            const screen = document.getElementById(screenId);
            if (screen) {
                screen.classList.add('hidden');
                screen.classList.remove('active');
            }
        });
    },

    /**
     * Show welcome screen (Home)
     */
    showWelcomeScreen() {
        this.hideAllScreens();
        const welcomeScreen = document.getElementById('welcomeScreen');
        if (welcomeScreen) {
            welcomeScreen.classList.remove('hidden');
            welcomeScreen.classList.add('active');
        }

        // Force UI update
        if (window.UIManager && typeof window.UIManager.renderWelcomeScreen === 'function') {
            window.UIManager.renderWelcomeScreen(false);
        }

        // Update profile in store if available
        if (window.StickerStore && typeof window.StickerStore.updateActiveUserProfile === 'function') {
            window.StickerStore.updateActiveUserProfile();
        }

        // Update global buttons
        this.updateGlobalNavButtons();
    },

    showConfigScreen() {
        // Detener audio al cambiar de pantalla
        if (window.stopAudio) window.stopAudio();

        this.hideAllScreens();
        const configScreen = document.getElementById('configScreen');
        if (configScreen) {
            configScreen.classList.remove('hidden');
            configScreen.classList.add('active');
        }

        if (window.goToDashboard) {
            window.goToDashboard();
        } else if (window.goToStep) {
            window.goToStep(1);
        }

        console.log('📋 Pantalla de configuración mostrada');
    },

    resetApp() {
        console.log('🔄 Reiniciando aplicación...');

        // Resetear variables de configuración
        if (store.config) {
            store.config.problemType = null;
            store.config.level = null;
            store.config.context = null;
            store.config.currentStep = 1;
            store.config.contextExplicit = true; // reset to explicit so old wizard still shows narrative
        }

        // Resetear estado de sesión
        this.state.problems = [];
        this.state.currentProblem = 0;
        this.state.answers = {};
        this.state.score = 0;
        this.state.solvedProblemIds = [];
        this.state.startTime = null;
        this.state.endTime = null;

        // Limpiar selecciones visuales
        document.querySelectorAll('.option-card, .context-card').forEach(card => {
            card.classList.remove('selected');
        });

        // Detener audio
        if (window.stopAudio) window.stopAudio();

        this.showWelcomeScreen();
    },

    goBackGlobal() {
        console.log('🔙 Navegación global hacia atrás');
        if (window.stopAudio) window.stopAudio();

        // 1. PRIORIDAD: Callbacks de retorno (Mapa/Tienda -> Modal)
        if (typeof this.onMapCloseCallback === 'function') {
            const cb = this.onMapCloseCallback;
            this.onMapCloseCallback = null;
            cb();
            return;
        }

        if (typeof this.onStoreCloseCallback === 'function') {
            const cb = this.onStoreCloseCallback;
            this.onStoreCloseCallback = null;
            cb();
            return;
        }

        const mapScreen = document.getElementById('mapScreen');
        if (mapScreen && mapScreen.classList.contains('active')) {
            this.showWelcomeScreen();
            return;
        }

        const loginScreen = document.getElementById('loginScreen');
        if (loginScreen && loginScreen.classList.contains('active')) {
            this.showWelcomeScreen();
            return;
        }

        const storeScreen = document.getElementById('storeScreen');
        if (storeScreen && storeScreen.classList.contains('active')) {
            this.showWelcomeScreen();
            return;
        }

        const narrativeScreen = document.getElementById('narrativeScreen');
        if (narrativeScreen && narrativeScreen.classList.contains('active')) {
            this.showConfigScreen();
            if (window.ConfigManager) window.ConfigManager.goToDashboard();
            return;
        }

        const configScreen = document.getElementById('configScreen');
        if (configScreen && configScreen.classList.contains('active')) {
            if (store.config && store.config.currentStep > 1) {
                if (window.goToStep) window.goToStep(store.config.currentStep - 1);
            } else {
                this.resetApp();
            }
            return;
        }

        // Fallback total
        this.showWelcomeScreen();
    },

    updateGlobalNavButtons() {
        const backBtn = document.getElementById('homeBtnGlobal');
        if (!backBtn) return;

        const narrativeScreen = document.getElementById('narrativeScreen');
        const isNarrativeActive = narrativeScreen && narrativeScreen.classList.contains('active') && !narrativeScreen.classList.contains('hidden');

        const problemScreen = document.getElementById('problemScreen');
        const isProblemActive = problemScreen && problemScreen.classList.contains('active') && !problemScreen.classList.contains('hidden');

        const mapScreen = document.getElementById('mapScreen');
        const isMapActive = mapScreen && mapScreen.classList.contains('active') && !mapScreen.classList.contains('hidden');

        const loginScreen = document.getElementById('loginScreen');
        const isLoginActive = loginScreen && loginScreen.classList.contains('active') && !loginScreen.classList.contains('hidden');

        const configScreen = document.getElementById('configScreen');
        const isConfigActive = configScreen && configScreen.classList.contains('active') && !configScreen.classList.contains('hidden');

        const isStep2Or3 = isConfigActive && store.config && (store.config.currentStep === 2 || store.config.currentStep === 3);

        // Mostrar botón si estamos en un estado que permite volver O si hay callback
        if (this.onMapCloseCallback || this.onStoreCloseCallback || isMapActive || isStep2Or3 || isNarrativeActive || isLoginActive) {
            backBtn.classList.add('visible');
        } else {
            backBtn.classList.remove('visible');
        }
    },

    showMapScreen(onCloseCallback = null) {
        console.log('🗺️ Mostrando pantalla del mapa');
        // Detener audio al cambiar de pantalla
        if (window.stopAudio) window.stopAudio();

        if (onCloseCallback) this.onMapCloseCallback = onCloseCallback;

        this.hideAllScreens();
        const mapScreen = document.getElementById('mapScreen');
        if (mapScreen) {
            mapScreen.classList.remove('hidden');
            mapScreen.classList.add('active');

            const container = document.getElementById('adventureMapContainer');
            if (container && window.AdventureMap) {
                window.AdventureMap.render(container);
            }

            // Ensure global back button is updated
            this.updateGlobalNavButtons();
        }
    },

    async showNarrativeIntro() {
        // Skip narrative screen if user did NOT explicitly choose a context
        // (i.e. it was randomly assigned by QuickConfigModal or not set at all)
        const skipNarrative = !store.config || store.config.contextExplicit === false;

        if (skipNarrative) {
            console.log('⏭️ Saltando pantalla narrativa (contexto no elegido explícitamente)');
            this.showProblemScreen();
            return;
        }

        this.hideAllScreens();
        const narrativeScreen = document.getElementById('narrativeScreen');
        if (narrativeScreen) {
            narrativeScreen.classList.remove('hidden');
            narrativeScreen.classList.add('active');
        }

        if (window.NarrativeManager && store.config) {
            await window.NarrativeManager.showIntro(store.config.context);
            this.showProblemScreen();
        } else {
            this.showProblemScreen();
        }
    },

    showProblemScreen() {
        this.hideAllScreens();
        const problemScreen = document.getElementById('problemScreen');
        if (problemScreen) {
            problemScreen.classList.remove('hidden');
            problemScreen.classList.add('active');
        }

        this.updateGlobalNavButtons();

        // El resto de la lógica (displayProblem) debe llamarse desde fuera o aquí
        if (window.displayProblem) {
            window.displayProblem(this.state.currentProblem !== undefined ? this.state.currentProblem : 0);
        }

        // [NEW] Start tracking time for the current problem
        const currentProbIndex = this.state.currentProblem !== undefined ? this.state.currentProblem : 0;
        if (window.GamificationService && this.state.problems[currentProbIndex]) {
            const probId = this.state.problems[currentProbIndex].id || `prob_${Date.now()}`;
            window.GamificationService.startProblem(probId);
        }
    },

    showSummaryScreen() {
        // Detener audio al cambiar de pantalla
        if (window.stopAudio) window.stopAudio();

        // [SCORM] Marcar lección como completada
        if (window.ScormManager) window.ScormManager.complete();

        this.hideAllScreens();
        const summaryScreen = document.getElementById('summaryScreen');
        if (summaryScreen) {
            summaryScreen.classList.remove('hidden');
            summaryScreen.classList.add('active');
        }

        if (window.renderSummary) window.renderSummary();
    },

    // --- UNIQUE PROBLEM LOGIC ---
    recordSolvedProblem(problemId) {
        if (!problemId) return;
        if (!this.state.solvedProblemIds.includes(problemId)) {
            this.state.solvedProblemIds.push(problemId);
            console.log(`✅ Problem ${problemId} marked as solved. Total: ${this.state.solvedProblemIds.length}`);
        }
    },

    isProblemSolved(problemId) {
        return this.state.solvedProblemIds && this.state.solvedProblemIds.includes(problemId);
    },

    getSolvedCount() {
        return this.state.solvedProblemIds.length;
    }
};

// Aliases para compatibilidad
window.hideAllScreens = () => window.SessionManager.hideAllScreens();
window.showWelcomeScreen = () => window.SessionManager.showWelcomeScreen();
window.showConfigScreen = () => window.SessionManager.showConfigScreen();
window.resetApp = () => window.SessionManager.resetApp();
window.goBackGlobal = () => window.SessionManager.goBackGlobal();
window.updateGlobalNavButtons = () => window.SessionManager.updateGlobalNavButtons();
window.showMapScreen = (cb) => window.SessionManager.showMapScreen(cb);
window.showNarrativeIntro = () => window.SessionManager.showNarrativeIntro();
window.showProblemScreen = () => window.SessionManager.showProblemScreen();
window.showSummaryScreen = () => window.SessionManager.showSummaryScreen();
window.finishConfiguration = async () => {
    console.log('🏁 Finalizando configuración...');

    // Generate problems based on configuration
    if (window.generateProblemsAsync && store.config) {
        try {
            console.log('📊 Generando problemas para:', store.config);
            const problems = await window.generateProblemsAsync(store.config);
            if (store.state) {
                store.state.problems = problems;
                store.state.currentProblem = 0; // RESET: Empezar desde el primero
                store.state.answers = {};       // RESET: Limpiar respuestas anteriores
                console.log(`✅ ${problems.length} problemas asignados. Reiniciando secuencia.`);
            }
        } catch (error) {
            console.error('❌ Error generando problemas:', error);
        }
    } else {
        console.warn('⚠️ generateProblemsAsync no disponible o configuración incompleta');
    }

    // Ir directamente a la pantalla del problema (la introducción solo se muestra bajo demanda al pulsar 'Introducción / Contexto')
    window.SessionManager.showProblemScreen();
};

// Inicialización
window.SessionManager = SessionManager;
window.SessionManager.init();
